import type { HTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'

interface SearchPopoverFrameProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export function SearchPopoverFrame({ children, className, ...props }: SearchPopoverFrameProps) {
  return (
    <div
      {...props}
      className={clsx(
        'surface-default border-default shadow-lg flex flex-col overflow-hidden rounded-lg border',
        className
      )}
    >
      {children}
    </div>
  )
}

interface SearchPopoverFilterItem<Key extends string> {
  key: Key
  label: string
  count?: number
  countSuffix?: string
}

interface SearchPopoverFiltersProps<Key extends string> {
  items: SearchPopoverFilterItem<Key>[]
  activeKey: Key
  onSelect: (key: Key) => void
  ariaLabel: string
}

export function SearchPopoverFilters<Key extends string>({
  items,
  activeKey,
  onSelect,
  ariaLabel,
}: SearchPopoverFiltersProps<Key>) {
  return (
    <div
      className="no-scrollbar border-default flex shrink-0 items-center gap-2 overflow-x-auto border-b px-3 py-2"
      role="tablist"
      aria-label={ariaLabel}
    >
      {items.map((item) => {
        const selected = activeKey === item.key
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={selected}
            className={clsx(
              'h-6 shrink-0 rounded border px-2.5 text-[11px] font-semibold transition-colors',
              selected
                ? 'border-[color:var(--border-info)] bg-[color:var(--surface-info-subtle)] text-default'
                : 'border-default bg-[color:var(--bg-muted)] text-subtle hover:bg-[color:var(--bg-lighter)]'
            )}
            onClick={() => onSelect(item.key)}
          >
            <span className="inline-flex items-center gap-1">
              <span>{item.label}</span>
              {item.count !== undefined && item.count > 0 && (
                <span className="font-medium text-muted">
                  {formatPopoverCount(item.count)}
                  {item.countSuffix ?? ''}
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

interface SearchPopoverRowProps {
  label: ReactNode
  meta?: ReactNode
  leading?: ReactNode
  status?: ReactNode
  trailing?: ReactNode
  active?: boolean
  onClick: () => void
  onPointerEnter?: () => void
}

interface SearchPopoverSectionHeaderProps {
  title: ReactNode
  count: number
  countSuffix?: string
  icon?: ReactNode
  active?: boolean
  collapsed?: boolean
  onClick?: () => void
  onPointerEnter?: () => void
  className?: string
  style?: HTMLAttributes<HTMLDivElement>['style']
}

export function SearchPopoverSectionHeader({
  title,
  count,
  countSuffix,
  icon,
  active = false,
  collapsed = false,
  onClick,
  onPointerEnter,
  className,
  style,
}: SearchPopoverSectionHeaderProps) {
  const content = (
    <>
      <span className="flex min-w-0 items-center gap-2 text-[11px] font-semibold text-default">
        {icon}
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate">{title}</span>
          <span className="shrink-0 font-normal text-muted">
            {formatPopoverFullCount(count)}
            {countSuffix}
          </span>
        </span>
      </span>
      {onClick && (
        <ChevronDownIcon
          className={clsx('shrink-0 text-subtle transition-transform', collapsed && '-rotate-90')}
        />
      )}
    </>
  )

  return (
    <div
      className={clsx(
        "surface-default sticky top-3 isolate -mx-3 px-3 before:pointer-events-none before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 before:bg-[color:var(--surface-default)] before:content-[''] after:pointer-events-none after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-1 after:bg-[color:var(--surface-default)] after:content-['']",
        className
      )}
      style={style}
    >
      {onClick ? (
        <button
          type="button"
          data-active={active ? 'true' : undefined}
          className={clsx(
            'flex h-7 w-full items-center justify-between rounded bg-[color:var(--bg-muted)] px-3 text-left transition-colors hover:bg-[color:color-mix(in_srgb,var(--bg-muted)_84%,var(--border-default))] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-info',
            active && 'bg-[color:color-mix(in_srgb,var(--bg-muted)_84%,var(--border-default))]'
          )}
          onPointerEnter={onPointerEnter}
          onClick={onClick}
          aria-expanded={!collapsed}
        >
          {content}
        </button>
      ) : (
        <div className="flex h-7 w-full items-center justify-between rounded bg-[color:var(--bg-muted)] px-3 text-left">
          {content}
        </div>
      )}
    </div>
  )
}

export function SearchPopoverRow({
  label,
  meta,
  leading,
  status,
  trailing,
  active,
  onClick,
  onPointerEnter,
}: SearchPopoverRowProps) {
  return (
    <button
      type="button"
      data-active={active ? 'true' : undefined}
      className={clsx(
        'group flex h-7 w-full items-center gap-3 rounded px-3 text-left transition-colors hover:bg-[color:color-mix(in_srgb,var(--bg-muted)_34%,var(--surface-default))] focus:outline-none',
        active && 'bg-[color:color-mix(in_srgb,var(--bg-muted)_34%,var(--surface-default))]'
      )}
      onPointerEnter={onPointerEnter}
      onClick={onClick}
    >
      <span className="flex min-w-0 flex-1 items-center">
        {leading}
        <span className="min-w-0 max-w-[52%] shrink-0 truncate text-[11px] font-semibold text-default">
          {label}
        </span>
        {meta}
      </span>
      {(status || trailing) && (
        <span className="flex shrink-0 items-center gap-2">
          {status}
          {trailing}
        </span>
      )}
    </button>
  )
}

export function SearchPopoverEmpty({ children }: { children: ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-subtle">{children}</div>
}

function formatPopoverCount(count: number) {
  if (count < 1000) return String(count)
  if (count < 1_000_000) return `${Math.floor(count / 100) / 10}K`
  if (count < 1_000_000_000) return `${Math.floor(count / 100_000) / 10}M`
  if (count < 1_000_000_000_000) return `${Math.floor(count / 100_000_000) / 10}B`
  return `${Math.floor(count / 100_000_000_000) / 10}T`
}

function formatPopoverFullCount(count: number) {
  return count.toLocaleString()
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
      <path
        d="M3 4.5 6 7.5 9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
